import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './store'

type TeamsAccessState = {
  isFetching: boolean;

  /**
   * Force fetch count to prevent multiple state updates for thunk
   * */
  fetchCount: number;

  teamsAccess: boolean;
  /**
   * NOTE: I must ensure that teamsId is set before teamsAccess is calculated
   * */
  teamsId: string | null;
  userId: string | null;
};

const initialState: TeamsAccessState = {
  isFetching: false,
  fetchCount: 0,
  teamsAccess: false,
  teamsId: null,
  userId: null,
};

export const teamsAccessSlice = createSlice({
  name: 'teamsAccess',
  initialState,
  reducers: {
    setTeamsAccess: (state, action: PayloadAction<boolean>) => {
      state.teamsAccess = action.payload;
    },
    setTeamsId: (state, action: PayloadAction<string | null>) => {
      state.teamsId = action.payload;
    },
    setUserId: (state, action: PayloadAction<string | null>) => {
      state.userId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamsAccessAsync.pending, (state) => {
        console.log('[fetchTeamsAccessAsync] Fetching teams access...');
        state.isFetching = true;
      })
      .addCase(fetchTeamsAccessAsync.rejected, (state) => {
        console.error('[fetchTeamsAccessAsync] Failed to fetch teams access.');
        state.isFetching = false;
      })
      .addCase(fetchTeamsAccessAsync.fulfilled, (state, { payload }) => {
        /**
         * Prevent multiple state updates
         * */
        if (state.fetchCount > 1) {
          return;
        }

        // Increment fetch count
        state.fetchCount += 1;

        console.log('[fetchTeamsAccessAsync] Successfully fetched teams access.');
        /**
         * Based on the response, determine if user has access to the team
         * */
        if (payload.success === "yes") {
          const tenantData = payload.teamsJoinedResponse.value.map(x => x.id);

          // Ensure teams id is set
          if (state.teamsId !== null) {
            const hasAccess = tenantData.includes(state.teamsId);
            if (hasAccess) {
              state.teamsAccess = true;
            } else {
              state.teamsAccess = false;
            }
          }
        }
        state.isFetching = false;
      })
  },
});

type TeamsJoinedResponse = {
  '@odata.context': string;
  '@odata.count': number;
  value: Array<{
    id: string;
    createdDateTime: string | null;
    displayName: string;
    description: string | null;
    internalId: string | null;
    classification: string | null;
    specialization: string | null;
    visibility: string | null;
    webUrl: string | null;
    isArchived: boolean;
    tenantId: string;
    isMembershipLimitedToOwners: boolean | null;
    memberSettings: any | null;
    guestSettings: any | null;
    messagingSettings: any | null;
    funSettings: any | null;
    discoverySettings: any | null;
    tagSettings: any | null;
    summary: any | null;
  }>;
};


/**
 * Async thunk to fetch Teams access information
 * */
export const fetchTeamsAccessAsync = createAsyncThunk(
  "teamsAccess/fetchTeamsAccess",
  async (args: {
    accessToken: string;
  }) => {
    const { accessToken } = args;
    const url = 'https://graph.microsoft.com/v1.0/me/joinedTeams';
    try {
      const res = await fetch(url, {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        },
      });
      /**
       * FIXME: Have 401/403 status codes
       * */
      if (res.status === 200) {
        const resultJson: TeamsJoinedResponse = await res.json();
        return {
          success: "yes" as const,
          teamsJoinedResponse: resultJson,
        };
      } else {
        return {
          success: "no" as const,
          teamsJoinedResponse: null,
        };
      }
    } catch (e) {
      console.error('Error fetching teams access:', e);
      return {
        success: "no" as const,
        teamsJoinedResponse: null,
      };
    }
  },
);

export const {
  setTeamsAccess, setTeamsId, setUserId
} = teamsAccessSlice.actions;

export const selectTeamsAccess = (state: RootState) => state.teamsAccess;
export default teamsAccessSlice.reducer;
